import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const TONES = {
  purple: { ring: 'border-purple-500/20 border-t-purple-500', icon: 'text-purple-500', bar: 'bg-purple-500', bg: 'bg-purple-500/10' },
  teal: { ring: 'border-teal-500/20 border-t-teal-500', icon: 'text-teal-500', bar: 'bg-teal-500', bg: 'bg-teal-500/10' },
};

export const ReportLoading = () => (
  <div className="flex items-center justify-center py-16" data-testid="report-loading">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

export const ReportGenerating = ({ Icon, title, messages, tone = 'purple', testid }) => {
  const [i, setI] = useState(0);
  const t = TONES[tone];
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % messages.length), 4000);
    return () => clearInterval(id);
  }, [messages.length]);
  return (
    <div className="flex flex-col items-center justify-center py-16 space-y-6" data-testid={testid}>
      <div className="relative">
        <div className={`w-20 h-20 rounded-full border-4 animate-spin ${t.ring}`} />
        <Icon className={`h-8 w-8 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${t.icon}`} />
      </div>
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
        <p className="text-sm text-muted-foreground animate-pulse max-w-md">{messages[i]}</p>
        <p className="text-xs text-muted-foreground">Created automatically for every job — usually ready within a few minutes. You can leave this page and come back.</p>
      </div>
      <div className="w-64 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full animate-pulse ${t.bar}`} style={{ width: '60%' }} />
      </div>
    </div>
  );
};

export const ReportEmpty = ({ Icon, title, text, tone = 'purple', failed, onGenerate, label, readOnly, testid, buttonTestid }) => {
  const t = TONES[tone];
  return (
    <div className="flex flex-col items-center justify-center py-16 space-y-6" data-testid={testid}>
      <div className={`w-20 h-20 rounded-full flex items-center justify-center ${t.bg}`}><Icon className={`h-10 w-10 ${t.icon}`} /></div>
      <div className="text-center space-y-2 max-w-md">
        <h3 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
        <p className="text-sm text-muted-foreground">{text}</p>
      </div>
      {failed && (
        <Badge variant="destructive" className="gap-1" data-testid={`${testid}-failed`}>
          <AlertTriangle className="h-3 w-3" /> Generation failed — please try again
        </Badge>
      )}
      {!readOnly && (
        <Button onClick={onGenerate} className="gap-2" size="lg" data-testid={buttonTestid}>
          <Zap className="h-4 w-4" /> {label}
        </Button>
      )}
    </div>
  );
};

export const RegenerateButton = ({ onClick, running, label, testid }) => (
  <Button variant="outline" size="sm" className="gap-1.5" onClick={onClick} disabled={running} data-testid={testid}>
    <RefreshCw className={`h-3.5 w-3.5 ${running ? 'animate-spin' : ''}`} /> {running ? 'Regenerating…' : label}
  </Button>
);

export const RegeneratingNote = ({ testid }) => (
  <div className="flex items-center gap-2 p-3 rounded-lg border border-indigo-500/20 bg-indigo-500/5 text-sm text-indigo-700 dark:text-indigo-300" data-testid={testid}>
    <Loader2 className="h-4 w-4 animate-spin shrink-0" />
    A fresh version is being generated. This one stays here, in shared links and in the PDF until the new one is ready.
  </div>
);
